import { useState } from 'react';

const SITE_URL = import.meta.env.VITE_SITE_URL || '';

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const contactSchema = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contact A&S Prestige Cleaning Service',
    url: `${SITE_URL}/contact`,
    description: 'Contact A&S Prestige Cleaning Service for a free quote on residential or commercial cleaning in Port St. Lucie and Martin County, FL.',
    mainEntity: {
      '@type': 'CleaningService',
      name: 'A&S Prestige Cleaning Service',
      url: SITE_URL,
      telephone: '+15618461326',
      email: 'asprestigecleaningservice62@gmail.com',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+15618461326',
        email: 'asprestigecleaningservice62@gmail.com',
        contactType: 'customer service',
        availableLanguage: 'English',
        hoursAvailable: {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '08:00',
          closes: '17:00',
        },
      },
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Port St. Lucie',
        addressRegion: 'FL',
        addressCountry: 'US',
      },
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Contact', item: `${SITE_URL}/contact` },
      ],
    },
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement).value;
    if (message.length > 500) return;

    setLoading(true);
    const formData = new URLSearchParams();
    const elements = form.elements;
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i] as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (el.name && el.value) {
        if (el.tagName === 'SELECT') {
          formData.append(el.name, (el as HTMLSelectElement).options[(el as HTMLSelectElement).selectedIndex].text);
        } else {
          formData.append(el.name, el.value);
        }
      }
    }

    try {
      await fetch('https://readdy.ai/api/form/d70407d1l1ubpb129nr0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      setSubmitted(true);
    } catch {
      // Silent fail — still show success
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main>
      <title>Contact A&S Cleaning Service | Free Quote Port St. Lucie &amp; Martin County</title>
      <meta name="description" content="Get in touch with A&S Cleaning Service for a free cleaning estimate in Port St. Lucie and Martin County, FL. Call (561) 846-1326 or fill out our form — we respond within 24 hours." />
      <meta name="keywords" content="contact cleaning service port st lucie, free cleaning quote florida, cleaning estimate martin county, book cleaning service stuart fl" />
      <link rel="canonical" href={`${SITE_URL}/contact`} />
      <meta property="og:title" content="Contact A&S Cleaning Service | Free Quote FL" />
      <meta property="og:description" content="Request a free cleaning estimate in Port St. Lucie and Martin County, FL. Fast response, no obligation." />
      <meta property="og:url" content={`${SITE_URL}/contact`} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(contactSchema) }}
      />

      {/* Hero */}
      <section className="relative pt-32 pb-20">
        <img
          src="https://public.readdy.ai/ai/img_res/def028c83b794f957f4ccdc6dcc9c580.jpg"
          alt="Contact A&S Cleaning Service"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60" />
        <div className="relative z-10 text-center px-6 max-w-3xl mx-auto">
          <span className="inline-block bg-emerald-600/90 text-white text-xs font-bold tracking-widest uppercase px-5 py-2 rounded-full mb-5">
            Get In Touch
          </span>
          <h1 className="text-5xl lg:text-6xl font-extrabold text-white mb-5 leading-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Contact Us
          </h1>
          <p className="text-gray-200 text-lg max-w-xl mx-auto">
            Ready to book a cleaning or have a question? We&apos;re here to help you find the perfect solution for your home or business.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-14 items-start">
          {/* Contact Info */}
          <div>
            <span className="text-emerald-600 font-bold text-sm tracking-widest uppercase">Reach Us Anytime</span>
            <h2 className="text-3xl font-extrabold text-gray-900 mt-3 mb-8" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              We&apos;d Love to Hear from You
            </h2>
            <ul className="space-y-6 mb-10">
              {[
                { icon: 'ri-phone-line', label: 'Phone', value: '561-846-1326', link: 'tel:+17725550100' },
                { icon: 'ri-mail-line', label: 'Email', value: 'asprestigecleaningservice62@gmail.com', link: 'mailto:asprestigecleaningservice62@gmail.com' },
                { icon: 'ri-map-pin-line', label: 'Service Area', value: 'Port St. Lucie & Martin County, FL', link: null },
                { icon: 'ri-time-line', label: 'Business Hours', value: 'Mon–Fri: 8am–5pm | Sat: 9am–4pm', link: null },
              ].map((item) => (
                <li key={item.label} className="flex items-start gap-5">
                  <div className="w-12 h-12 flex items-center justify-center bg-emerald-100 rounded-xl shrink-0">
                    <i className={`${item.icon} text-emerald-600 text-xl`} />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-0.5">{item.label}</p>
                    {item.link ? (
                      <a href={item.link} className="text-gray-800 font-semibold hover:text-emerald-600 transition-colors cursor-pointer">
                        {item.value}
                      </a>
                    ) : (
                      <p className="text-gray-800 font-semibold">{item.value}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            {/* Testimonial */}
            <div className="bg-gray-50 rounded-2xl p-7">
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => <i key={i} className="ri-star-fill text-amber-400" />)}
              </div>
              <p className="text-gray-600 text-sm leading-relaxed italic">
                &ldquo;From the first call to the final clean, A&amp;S was professional, responsive, and thorough. My home sparkles and I&apos;ll definitely be booking again. Best cleaning service in all of Port St. Lucie!&rdquo;
              </p>
              <div className="flex items-center gap-3 mt-5">
                <img
                  src="https://readdy.ai/api/search-image?query=smiling%20professional%20woman%20portrait%20headshot%20warm%20natural%20lighting%20clean%20background%20south%20florida&width=60&height=60&seq=ctrev1&orientation=squarish"
                  alt="Patricia R."
                  className="w-10 h-10 rounded-full object-cover object-top"
                />
                <div>
                  <p className="font-bold text-gray-900 text-sm">Patricia R.</p>
                  <p className="text-gray-400 text-xs">Port St. Lucie, FL</p>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="bg-gray-50 rounded-2xl p-8 lg:p-10">
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-5">
                  <i className="ri-check-double-line text-emerald-600 text-3xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Message Sent!</h3>
                <p className="text-gray-500 text-sm leading-relaxed max-w-sm mx-auto">
                  Thank you for reaching out to A&amp;S Cleaning Service. One of our team members will be in touch with you within one business day.
                </p>
              </div>
            ) : (
              <>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Request More Information</h3>
                <p className="text-gray-500 text-sm mb-7">Fill out the form below and we&apos;ll get back to you with a free estimate within 24 hours.</p>

                <form
                  data-readdy-form
                  id="contact-form"
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-bold text-gray-600 uppercase tracking-wider block mb-2">First Name *</label>
                      <input
                        type="text"
                        name="first_name"
                        required
                        placeholder="Maria"
                        className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-emerald-500 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-gray-600 uppercase tracking-wider block mb-2">Last Name *</label>
                      <input
                        type="text"
                        name="last_name"
                        required
                        placeholder="Gonzalez"
                        className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-emerald-500 transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-wider block mb-2">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="maria@example.com"
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-emerald-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-wider block mb-2">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="(772) 555-1234"
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-emerald-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-wider block mb-2">City</label>
                    <select
                      name="city"
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:border-emerald-500 transition-colors cursor-pointer"
                    >
                      <option value="">Select your city...</option>
                      <option>Port St. Lucie</option>
                      <option>Stuart</option>
                      <option>Fort Pierce</option>
                      <option>Jensen Beach</option>
                      <option>Palm City</option>
                      <option>Hobe Sound</option>
                      <option>Tradition</option>
                      <option>Port Salerno</option>
                      <option>St. Lucie West</option>
                      <option>Indiantown</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-wider block mb-2">Service Type</label>
                    <select
                      name="service_type"
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:border-emerald-500 transition-colors cursor-pointer"
                    >
                      <option value="">Select a service...</option>
                      <option>Residential Cleaning</option>
                      <option>Commercial Cleaning</option>
                      <option>Deep Cleaning</option>
                      <option>Move In / Move Out</option>
                      <option>Post-Construction Cleaning</option>
                      <option>Recurring Maintenance Plan</option>
                      <option>Other / Not Sure</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-wider block mb-2">
                      Message <span className="text-gray-400 font-normal">({charCount}/500)</span>
                    </label>
                    <textarea
                      name="message"
                      rows={4}
                      maxLength={500}
                      placeholder="Tell us about your cleaning needs, property size, frequency, or any questions you have..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-emerald-500 transition-colors resize-none"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading || charCount > 500}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white font-bold py-4 rounded-xl transition-colors whitespace-nowrap cursor-pointer text-sm"
                  >
                    {loading ? 'Sending...' : 'Send Request'}
                  </button>
                  <p className="text-center text-xs text-gray-400">
                    We&apos;ll respond within 1 business day. No spam, ever.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
